import React, { useState, useEffect } from 'react';
import { Play, Info, Volume2, VolumeX } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { LazyBackgroundImage } from './LazyImage';

const HeroSection = ({ featuredMovie, onPlay, onOpenDetails }) => {
  const [isMuted, setIsMuted] = useState(true);
  const [showVideo, setShowVideo] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    // Preload the hero image
    if (featuredMovie?.backdrop) {
      const img = new Image();
      img.src = featuredMovie.backdrop;
      img.onload = () => setImageLoaded(true);
    }
  }, [featuredMovie?.backdrop]);

  useEffect(() => {
    // Only show video after image is loaded and a delay
    if (!imageLoaded) return;
    
    const timer = setTimeout(() => {
      setShowVideo(true);
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [imageLoaded]);

  if (!featuredMovie) return null;

  return (
    <div className="relative h-screen overflow-hidden">
      {/* Background Image with lazy loading */}
      <LazyBackgroundImage
        src={featuredMovie.backdrop}
        className="absolute inset-0"
        placeholderColor="#0a0a0a"
        blurAmount={25}
        transitionDuration={800}
        overlay={false}
      >
        {/* Video overlay when ready */}
        {showVideo && featuredMovie.videoId && (
          <div className="absolute inset-0">
            <iframe
              src={`https://www.youtube.com/embed/${featuredMovie.videoId}?autoplay=1&mute=${isMuted ? 1 : 0}&controls=0&showinfo=0&rel=0&loop=1&playlist=${featuredMovie.videoId}&modestbranding=1&iv_load_policy=3`}
              className="w-full h-full object-cover scale-110"
              frameBorder="0"
              allow="autoplay; encrypted-media; fullscreen"
              allowFullScreen
              title={featuredMovie.title}
            />
          </div>
        )}
      </LazyBackgroundImage>

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/50 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent" />

      {/* Content */}
      <div className="absolute bottom-32 left-0 right-0 px-4">
        <div className="container mx-auto">
          <div className="max-w-2xl">
            {featuredMovie.isNetflixOriginal && (
              <Badge className="mb-4 bg-black/80 text-white text-sm font-bold px-3 py-1 border-0 flex items-center w-fit">
                <span className="text-red-500">I</span>
                <span className="text-orange-500 ml-1">G</span>
                <span className="text-yellow-500">o</span>
                <span className="text-green-500">t</span>
                <span className="text-blue-500 ml-2">S</span>
                <span className="text-indigo-500">o</span>
                <span className="text-purple-500">u</span>
                <span className="text-pink-500">l</span>
                <span className="text-white ml-2">ORIGINAL</span>
              </Badge>
            )}
            
            <h1 
              className="text-4xl md:text-6xl font-bold text-white mb-4 leading-tight"
              style={{
                opacity: imageLoaded ? 1 : 0,
                transform: imageLoaded ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 600ms ease-out, transform 600ms ease-out'
              }}
            >
              {featuredMovie.title}
            </h1>
            
            <p 
              className="text-lg text-gray-200 mb-6 line-clamp-3 max-w-xl"
              style={{
                opacity: imageLoaded ? 1 : 0,
                transform: imageLoaded ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 600ms ease-out 100ms, transform 600ms ease-out 100ms'
              }}
            >
              {featuredMovie.description}
            </p>

            <div 
              className="flex items-center gap-2 mb-8 text-sm text-gray-300"
              style={{
                opacity: imageLoaded ? 1 : 0,
                transition: 'opacity 600ms ease-out 200ms'
              }}
            >
              <Badge variant="outline" className="text-green-400 border-green-400">
                98% Match
              </Badge>
              <span>{featuredMovie.year}</span>
              <span>{featuredMovie.rating}</span>
              <span>{featuredMovie.duration}</span>
              <Badge variant="secondary" className="bg-gray-700 text-gray-300">
                {featuredMovie.genre}
              </Badge>
            </div>

            <div 
              className="flex items-center gap-4"
              style={{
                opacity: imageLoaded ? 1 : 0,
                transform: imageLoaded ? 'translateY(0)' : 'translateY(10px)',
                transition: 'opacity 600ms ease-out 300ms, transform 600ms ease-out 300ms'
              }}
            >
              <Button
                size="lg"
                onClick={() => onPlay(featuredMovie)}
                className="bg-white text-black hover:bg-gray-200 text-lg font-semibold px-8 py-3 rounded-md min-h-[48px] active:scale-95 transition-transform"
                data-testid="hero-play-btn"
              >
                <Play className="w-6 h-6 mr-2 fill-current" />
                Play
              </Button>
              
              <Button
                variant="secondary"
                size="lg"
                onClick={() => onOpenDetails(featuredMovie)}
                className="bg-gray-600/80 text-white hover:bg-gray-500/80 text-lg font-semibold px-8 py-3 rounded-md min-h-[48px] active:scale-95 transition-transform"
                data-testid="hero-info-btn"
              >
                <Info className="w-6 h-6 mr-2" />
                More Info
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mute/Unmute button */}
      {showVideo && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsMuted(!isMuted)}
          className="absolute bottom-32 right-8 bg-gray-800/80 hover:bg-gray-700 text-white rounded-full w-11 h-11 p-0 active:scale-95 transition-transform"
          data-testid="hero-mute-btn"
        >
          {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </Button>
      )}
    </div>
  );
};

export default HeroSection;
